import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal } from 'antd'
import { GoogleMap, Polygon as GooglePolygon, Marker } from '@react-google-maps/api'
import * as turf from '@turf/turf'
import type { Feature, Polygon } from 'geojson'
import { kml } from '@tmcw/togeojson'
import { GridMalha, GridPolygon, MalhaGrid } from './components/gerarMalha'
import Configuracoes from './components/config'
import { calcularCentroTurf, criarPoligono } from './meta'
import { calculoAreaCoord } from '../../../../../../../../../../../../services/Afins'
import { errorAlert, warningAlert } from '../../../../../../../../../../../../utils/alert'
import { Render } from '../../../../../../../../../../../../Components/CustomUi/render'
import Carregando from '../../../../../../../../../../../../Components/CustomUi/loading'
import { baixarGridKML, gerarArquivoShp } from './meta'


interface Props {
  isOpen: boolean
  onClose: () => void
  talhaoKml: any[]
  setTalhaoKml: React.Dispatch<React.SetStateAction<any[]>>
  setTalhaoArea?: React.Dispatch<React.SetStateAction<string>>
  setMalha: React.Dispatch<React.SetStateAction<any[]>>
  setMalhaShp: React.Dispatch<React.SetStateAction<any[]>>
  setPontosTotalMalha: React.Dispatch<React.SetStateAction<number>>
}

const ModalCriarMalha: React.FC<Props> = ({ isOpen, onClose, talhaoKml, setTalhaoKml, setTalhaoArea, setMalha, setMalhaShp, setPontosTotalMalha }) => {
  const mapRef = useRef<google.maps.Map | null>(null)
  const polygonRef = useRef<google.maps.Polygon | null>(null)

  const [loadingKmlAdjustment, setLoadingKmlAdjustment] = useState(false)


  const [kmlComCruzamento, setKmlComCruzamento] = useState(null)
  const [disable, setDisable] = useState(true)
  const [validoFile, setValidoFile] = useState(false)
  const [area, setArea] = useState([])
  const [editandoKml, setEditandoKml] = useState<boolean>(false)
  const [pontosKinks, setPontosKinks] = useState<{ lat: number; lng: number }[]>([])

  //GRID
  const [rotation, setRotation] = useState<number>(0)
  const [movendo, setMovendo] = useState<boolean>(false)
  const [areaHa, setAreaHa] = useState<number>(0)
  const [gridLevel, setGridLevel] = useState<string>('')
  const [position, setPosition] = useState<{ x: 0; y: 0 }>({ x: 0, y: 0 })
  const [grid, setGrid] = useState<GridMalha[]>([])
  const [gridOriginal, setGridOriginal] = useState<GridMalha[]>([])
  const [gridOriginalRotacao, setGridOriginalRotacao] = useState<GridMalha[]>([])
  const [malhaPolygon, setMalhaPolygon] = useState<GridPolygon[]>([])
  const [malhaPolygonOriginal, setMalhaPolygonOriginal] = useState<GridPolygon[]>([])
  const [tamanhoGridOriginal, setTamanhoGridOriginal] = useState<number>(0)
  const [abrirModal, setAbrirModal] = useState<boolean>(false)
  const [pontosSelecionados, setPontosSelecionados] = useState([])
  const [nomeTalhao, setNomeTalhao] = useState<string>('')
  const [clicou, setClicou] = useState<boolean>(false)
  const [mousePosition, setMousePosition] = useState<[number, number] | null>(null)
  const [mapClickEvent, setMapClickEvent] = useState<{ lat: number; lng: number; ts: number } | null>(null)

  //CONFIGURAÇÕES
  const [tab_container_atual, setTab_container_atual] = useState<string>('Mapa')
  const [novoPonto, setNovoPonto] = useState<boolean>(false)
  const [moverPonto, setMoverPonto] = useState<boolean>(false)
  const [removerPonto, setRemoverPonto] = useState<boolean>(false)
  const [selecionarPonto, setSelecionarPonto] = useState<boolean>(false)
  const [abaAtual, setAbaAtual] = useState<number>(0)
  const abasNome = ['Mapa', 'Pontos', 'Ordenacao']
  const [confirmado, setConfirmado] = useState<boolean>(false)
  const [arquivoSelecionado, setArquivoSelecionado] = useState<string>('')
  const [rotacaoInput, setRotacaoInput] = useState<string>('')
  const [reset, setReset] = useState(true)
  const [quadrante, setQuadrante] = useState<number>(0)

  const padrao = useMemo(() => [
    [-15.7801, -47.9292],
    [-15.7801, -47.9192],
    [-15.7701, -47.9192],
    [-15.7701, -47.9292],
    [-15.7801, -47.9292],
  ], [])

  const kmlCoordsLatLng = useMemo(() => {
    if (area.length <= 0) return padrao
    return area.map((item) => [item.lat, item.lng])
  }, [area, padrao])

  // ancora retorna [lng, lat] do turf — convertemos para {lat, lng}
  const ancoraRaw = useMemo(() => calcularCentroTurf(kmlCoordsLatLng), [kmlCoordsLatLng])
  const ancora = useMemo(() => {
    if (!ancoraRaw) return null
    return { lat: ancoraRaw[1], lng: ancoraRaw[0] }
  }, [ancoraRaw])

  const polygonKml = useMemo(() => criarPoligono(kmlCoordsLatLng), [kmlCoordsLatLng])

  // Se talhaoKml já tiver coordenadas, usa direto (sem precisar de upload KML)
  useEffect(() => {
    if (talhaoKml?.length > 0) {
      // Aceita tanto [{lat, lng}] quanto [[lat, lng]]
      const areaCoord = talhaoKml.map((item) => {
        if (Array.isArray(item)) {
          return { lat: Number(item[0]), lng: Number(item[1]) }
        }
        return { lat: Number(item.lat), lng: Number(item.lng) }
      })

      // Valida se todas as coordenadas são números válidos
      const valido = areaCoord.every((c) => !isNaN(c.lat) && !isNaN(c.lng))
      if (!valido) return

      setArea(areaCoord)
      const calcArea = calculoAreaCoord([areaCoord])
      setAreaHa(calcArea)
      if (calcArea > 0) {
        setValidoFile(true)
        setDisable(false)
      }
    }
  }, [talhaoKml])

  const aoCarregarArquivo = useCallback((rawData: string, base64Data) => {
    extrairCoordenadas(rawData)
  }, [])

  const converterXmlDeString = async (xmlString: string): Promise<Document> => {
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xmlString, 'application/xml')
    return xmlDoc
  }

  const extrairCoordenadas = async (kmlString: string) => {
    try {
      const domKml = await converterXmlDeString(kmlString)

      const dataGeoJson = kml(domKml)
      setNomeTalhao(dataGeoJson?.features[0]?.properties?.name)
      const talhoes = []

      const dados = dataGeoJson.features
      const filtrado = dados.filter((data) => data.geometry.type === 'Polygon')

      if (filtrado.length > 0) {
        for (let i = 0; i < filtrado.length; i += 1) {
          const fragment = filtrado[i]
          const talhaoPoly = []

          const coordinates = fragment.geometry.coordinates[0]

          for (let ii = 0; ii < coordinates.length; ii += 1) {
            const coord = coordinates[ii]

            talhaoPoly.push({ lat: coord[1], lng: coord[0] })
          }

          talhoes.push(talhaoPoly)
        }

        const areaCoord = talhoes[0].map((item) => {
          return { lat: item.lat, lng: item.lng }
        })
        setArea(areaCoord)
        setTalhaoKml(areaCoord)
        const calcArea = calculoAreaCoord([areaCoord])
        setAreaHa(calcArea)
        setTalhaoArea(String(calcArea))
        if (calcArea <= 0) {
          errorAlert('Arquivo Kml inválido!')
          setValidoFile(false)
          setTimeout(() => {
            setDisable(true)
          }, 1000)
        } else {
          setValidoFile(true)
          setTimeout(() => {
            setDisable(false)
          }, 1000)
        }
      } else {
        errorAlert('Arquivo kml inválido!')
        setValidoFile(false)
      }
    } catch (error) {
      console.error('Erro ao processar XML:', error)
    }
  }

  const verificarAvancar = useCallback(() => {
    const temValor = grid.some((item) => 'posicao' in item)
    const tamanhoGrid = grid.filter(({ center }) =>
      turf.booleanPointInPolygon(turf.point([center[1], center[0]]), polygonKml)
    )
    if (temValor && abaAtual === 2) {
      return setAbrirModal(true)
    }

    if (tamanhoGridOriginal !== tamanhoGrid.length && abaAtual === 1) {
      return setAbrirModal(true)
    }

    voltar()
  }, [grid, polygonKml, abaAtual, tamanhoGridOriginal])

  const avancar = useCallback(() => {
    if (abaAtual < abasNome.length - 1) {
      setAbaAtual((prev) => prev + 1)
    }
  }, [abaAtual, abasNome.length])

  const voltar = useCallback(() => {
    if (abaAtual > 0) {
      setAbaAtual((prev) => prev - 1)
    }
  }, [abaAtual])

  const resetarGrid = useCallback(() => {
    setReset(false)
    setGrid(gridOriginalRotacao)
    setPontosSelecionados([])
    setMousePosition(null)
  }, [gridOriginalRotacao])

  // Função para BAIXAR o arquivo (KML ou SHP) sem fechar o modal
  const baixarMalha = useCallback((tipo: string) => {
    if (tipo === 'Kml') {
      baixarGridKML(grid, nomeTalhao)
    } else {
      gerarArquivoShp(grid, nomeTalhao)
    }
  }, [grid, nomeTalhao])

  // Função para SALVAR a malha (envia dados para o pai e fecha o modal)
  const salvarMalha = useCallback(() => {
    const gridValido = grid.filter(g => !g.oculto)

    const pontosFormatados = gridValido.map((item, index) => ({
      INDICE: item.posicao ?? (index + 1),
      NOME: item.posicao ? String(item.posicao) : String(index + 1),
      LATITUDE: item.center[0],
      LONGITUDE: item.center[1],
      COR: null
    }))

    setMalha(pontosFormatados)

    const features = gridValido.map((item, index) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [String(item.center[1]), String(item.center[0])]
      },
      properties: {
        INDICE: String(item.posicao ?? (index + 1)),
        NOME: item.posicao ? String(item.posicao) : String(index + 1),
        LATITUDE: String(item.center[0]),
        LONGITUDE: String(item.center[1]),
        COMENTARIO: null,
        ICONE: null,
        ALTITUDE: null,
        DATA: null,
        ANGULO: null,
        DISPLAY: null,
        ZONA: null,
      }
    }))

    // @ts-ignore
    const malhaGeoJson: any = {
      type: 'FeatureCollection',
      features: features,
      fileName: nomeTalhao || 'Malha'
    }

    setMalhaShp(malhaGeoJson)
    setPontosTotalMalha(pontosFormatados.length)

    // Fechar modal e limpar estado
    setArquivoSelecionado('')
    setAbaAtual(0)
    setTab_container_atual('Mapa')
    setGrid([])
    setPontosSelecionados([])
    onClose()
  }, [grid, nomeTalhao, setMalha, setMalhaShp, onClose])

  useEffect(() => {
    if (confirmado) {
      if (abaAtual === 2) {
        voltar()
        setAbrirModal(false)
        setSelecionarPonto(false)
        resetarGrid()
        setArquivoSelecionado('')
        setTimeout(() => {
          setConfirmado(false)
        }, 500)
      }

      if (abaAtual === 1) {
        voltar()
        setAbrirModal(false)
        setGrid(gridOriginal)
        setRotation(0)
        setRotacaoInput('0')
        setNovoPonto(false)
        setMoverPonto(false)
        setRemoverPonto(false)
        setTimeout(() => {
          setConfirmado(false)
        }, 500)
      }
    }
  }, [confirmado])

  useEffect(() => {
    if (area.length > 0 && isOpen) {
      // Pequeno timeout para garantir que o "loading" apareça se foi setado
      const timer = setTimeout(() => {
        const kinks = turf.kinks(polygonKml)

        if (kinks.features.length > 0) {
          const pontos = kinks.features.map((f) => ({
            lat: f.geometry.coordinates[1],
            lng: f.geometry.coordinates[0],
          }))
          warningAlert(`O contorno possui ${pontos.length} vértice(s) sobreposto(s), habilite o editar contorno para ajustar`)
          setPontosKinks(pontos)
          setKmlComCruzamento(true)

          // NÃO seta setEditandoKml(false). 
          // Se o usuário está editando, continua. Se não está, continua.

          setLoadingKmlAdjustment(false)
          // warningAlert(`Contorno com ${pontos.length} cruzamento(s)!`)
          return
        }

        setPontosKinks([])
        setEditandoKml(false)
        setGrid([])
        setGridOriginal([])
        setGridOriginalRotacao([])
        setTamanhoGridOriginal(null)
        setClicou(false)
        setRotation(0)
        setKmlComCruzamento(false)
        setLoadingKmlAdjustment(false)
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [area, isOpen, polygonKml])

  // Handler para salvar contorno editado — chamado manualmente pelo botão
  const salvarContorno = useCallback(() => {
    if (!polygonRef.current) return
    setLoadingKmlAdjustment(true)
    requestAnimationFrame(() => {
      const path = polygonRef.current?.getPath()
      if (!path) return
      const novaArea: { lat: number; lng: number }[] = []
      for (let i = 0; i < path.getLength(); i++) {
        const pt = path.getAt(i)
        novaArea.push({ lat: pt.lat(), lng: pt.lng() })
      }
      // Garante que o polígono esteja fechado
      if (novaArea.length > 0) {
        const first = novaArea[0]
        const last = novaArea[novaArea.length - 1]
        if (first.lat !== last.lat || first.lng !== last.lng) {
          novaArea.push({ ...first })
        }
      }
      // Desativa modo edição antes de atualizar area para que o fitBounds centralize
      setEditandoKml(false)
      setArea(novaArea)
    })
  }, [])

  useEffect(() => {
    abasNome.forEach((item, index) => {
      if (index === abaAtual) {
        setTab_container_atual(item)
      }
    })

    if (abaAtual === 1) {
      setSelecionarPonto(false)
      setPontosSelecionados([])
    }

    if (abaAtual === 2) {
      setSelecionarPonto(true)
      setNovoPonto(false)
      setMoverPonto(false)
      setRemoverPonto(false)
    }
  }, [abaAtual])

  // Ajustar bounds do mapa quando as coordenadas mudam
  useEffect(() => {
    if (!mapRef.current || area.length === 0) return
    if (editandoKml) return

    const bounds = new google.maps.LatLngBounds()
    area.forEach(({ lat, lng }) => {
      bounds.extend({ lat, lng })
    })
    mapRef.current.fitBounds(bounds)
  }, [area, editandoKml])

  const center = useMemo(() => {
    if (area.length <= 0) return { lat: -14.235, lng: -51.925 }
    if (ancora) return ancora
    return { lat: kmlCoordsLatLng[0][0], lng: kmlCoordsLatLng[0][1] }
  }, [kmlCoordsLatLng, ancora, area])

  // Paths do polígono KML para o Google Maps
  const kmlPolygonPaths = useMemo(() => {
    return kmlCoordsLatLng.map(([lat, lng]) => ({ lat, lng }))
  }, [kmlCoordsLatLng])

  // Handler de clique no mapa — repassa para MalhaGrid via state
  const aoClicarNoMapa = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return
      setMapClickEvent({ lat: e.latLng.lat(), lng: e.latLng.lng(), ts: Date.now() })
    },
    []
  )

  const aoMoverMouseNoMapa = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return
      if (pontosSelecionados.length === 1) {
        setMousePosition([e.latLng.lat(), e.latLng.lng()])
      }
    },
    [pontosSelecionados]
  )

  const aoCarregarMapa = useCallback((map: google.maps.Map) => {
    mapRef.current = map

    // Aplica fitBounds se já tiver área
    if (area.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      area.forEach(({ lat, lng }) => {
        bounds.extend({ lat, lng })
      })
      map.fitBounds(bounds)
    }
  }, [area])

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title='Criar Malha'
      footer={null}
      width={'100vw'}
      style={{ top: 0 }}
      bodyStyle={{ padding: 0, height: '92vh', overflow: 'hidden' }}
      destroyOnClose
    >
      <div style={{ display: 'flex', position: 'relative', overflow: 'hidden', height: '100%' }}>
        <Configuracoes
          disable={disable}
          setDisable={setDisable}
          aoCarregarArquivo={aoCarregarArquivo}
          validoFile={validoFile}
          setValidoFile={setValidoFile}
          rotation={rotation}
          setRotation={setRotation}
          rotacaoInput={rotacaoInput}
          setRotacaoInput={setRotacaoInput}
          movendo={movendo}
          setMovendo={setMovendo}
          novoPonto={novoPonto}
          setNovoPonto={setNovoPonto}
          moverPonto={moverPonto}
          setMoverPonto={setMoverPonto}
          removerPonto={removerPonto}
          setRemoverPonto={setRemoverPonto}
          selecionarPonto={selecionarPonto}
          setSelecionarPonto={setSelecionarPonto}
          gridLevel={gridLevel}
          setGridLevel={setGridLevel}
          position={position}
          setPosition={setPosition}
          tab_container_atual={tab_container_atual}
          setTab_container_atual={setTab_container_atual}
          arquivoSelecionado={arquivoSelecionado}
          setArquivoSelecionado={setArquivoSelecionado}
          setGrid={setGrid}
          setGridOriginal={setGridOriginal}
          setGridOriginalRotacao={setGridOriginalRotacao}
          setMalhaPolygon={setMalhaPolygon}
          setMalhaPolygonOriginal={setMalhaPolygonOriginal}
          setTamanhoGridOriginal={setTamanhoGridOriginal}
          setQuadrante={setQuadrante}
          setClicou={setClicou}
          areaHectares={areaHa}
          polygon={polygonKml}
          kmlComCruzamento={kmlComCruzamento}
          editandoKml={editandoKml}
          setEditandoKml={setEditandoKml}
          grid={grid}
          abaAtual={abaAtual}
          abasNome={abasNome}
          pontosSelecionados={pontosSelecionados}
          area={area}
          areaTamanho={areaHa}
          quadrante={quadrante}
          avancar={avancar}
          voltar={verificarAvancar}
          resetarGrid={resetarGrid}
          nomeTalhao={nomeTalhao}
          salvarMalha={salvarMalha}
          baixarMalha={baixarMalha}
          salvarContorno={salvarContorno}
        />

        {/* @ts-ignore */}
        <GoogleMap
          onLoad={aoCarregarMapa}
          onClick={aoClicarNoMapa}
          onMouseMove={aoMoverMouseNoMapa}
          mapContainerStyle={{ height: '100%', width: '100%', position: 'relative', overflow: 'hidden' }}
          center={center}
          zoom={area.length > 0 ? 15 : 4}
          options={{
            mapTypeId: 'hybrid',
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            disableDefaultUI: false,
          }}
          onRightClick={() => { }}
        >
          <MalhaGrid
            polygon={polygonKml}
            novoPonto={novoPonto}
            moverPonto={moverPonto}
            removerPonto={removerPonto}
            selecionarPonto={selecionarPonto}
            rotation={rotation}
            isSliding={movendo}
            gridLevel={Number(gridLevel.replace(',', '.'))}
            moverHorizontal={position?.x}
            moverVertical={position?.y}
            tab_selecionado={tab_container_atual}
            abaAtual={abaAtual}
            grid={grid}
            setGrid={setGrid}
            gridOriginal={gridOriginal}
            setGridOriginal={setGridOriginal}
            gridOriginalRotacao={gridOriginalRotacao}
            setGridOriginalRotacao={setGridOriginalRotacao}
            setTamanhoGridOriginal={setTamanhoGridOriginal}
            abrirModal={abrirModal}
            setAbrirModal={setAbrirModal}
            confirmado={confirmado}
            setConfirmado={setConfirmado}
            pontosSelecionados={pontosSelecionados}
            setPontosSelecionados={setPontosSelecionados}
            mousePosition={mousePosition}
            setMousePosition={setMousePosition}
            ancora={ancora}
            mapClickEvent={mapClickEvent}
          />

          {/* Polígono do KML — editável quando em modo edição */}
          <GooglePolygon
            paths={kmlPolygonPaths}
            editable={editandoKml}
            draggable={false}
            onLoad={(polygon) => { polygonRef.current = polygon }}
            onUnmount={() => { polygonRef.current = null }}
            onRightClick={(e: any) => {
              if (!editandoKml) return
              if (e.vertex == null) return
              const vertex: number = e.vertex
              const path = polygonRef.current?.getPath()
              if (!path || path.getLength() <= 3) {
                warningAlert('O polígono precisa ter no mínimo 3 vértices!')
                return
              }
              path.removeAt(vertex)
            }}
            options={{
              fillOpacity: editandoKml ? 0.05 : 0,
              fillColor: editandoKml ? '#ff4444' : undefined,
              strokeColor: editandoKml ? '#ff4444' : kmlComCruzamento ? '#ff4444' : 'cyan',
              strokeWeight: editandoKml ? 3 : 2,
              clickable: editandoKml,
            }}
          />

          {/* Marcadores nos pontos de interseção — ocultos durante edição para não poluir */}
          {!editandoKml && pontosKinks.map((pt, i) => (
            <Marker
              key={`kink-${i}`}
              position={pt}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: 7,
                fillColor: '#ff0000',
                fillOpacity: 0.9,
                strokeColor: '#ffffff',
                strokeWeight: 2,
              }}
              label={{
                text: '✕',
                color: '#ffffff',
                fontSize: '7px',
                fontWeight: 'bold',
              }}
            />
          ))}
        </GoogleMap>
        {/* Overlay de Loading Ajustando Contorno */}
        <Render condition={loadingKmlAdjustment}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255,255,255,0.7)',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column'
          }}>
            <Carregando animation legenda='Ajustando contorno...' justifyContent='center' height={100} />
          </div>
        </Render>
      </div>
    </Modal>
  )
}

export default ModalCriarMalha
